import type { TableColumn } from "@components";
import {
	Badge,
	ButtonUtility,
	LoadingIndicator,
	TABLE_LISTING_STYLES,
	Table,
	TableCard,
} from "@components";
import { useQuery } from "@tanstack/react-query";
import {
	ChevronDown,
	ChevronUp,
	Download01,
	Edit05,
	LinkExternal01,
} from "@untitledui/icons";
import dayjs from "dayjs";
import { type ComponentPropsWithRef, type FC, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { Column } from "@/components/ui/table";
import { cx } from "@/utils/cx";
import {
	vehicleDocumentStatusColor,
	vehicleDocumentStatusDotColor,
	vehicleDocumentStatusLabel,
	vehicleDocumentTypeIdLabel,
} from "../handlers";
import { useVehicleDocumentUrl } from "../hooks";
import { getVehicleDocumentsHistory } from "../services";
import type { VehicleDocumentStatus } from "../types";
import type { VehicleDocument } from "../types/vehicle-document.types";
import { VehicleDocumentEmpty } from "./vehicle-document-empty";

type VehicleDocumentsSectionProps = {
	onAdd: () => void;
	onEdit: (doc: VehicleDocument) => void;
	isDrawerOpen: boolean;
	editingDocument: VehicleDocument | null;
	onCloseDrawer: () => void;
	onSubmitDocument: (doc: VehicleDocument) => void;
};

export function VehicleDocumentsSection({
	onAdd,
	onEdit,
}: VehicleDocumentsSectionProps) {
	const [historicoExpanded, setHistóricoExpanded] = useState(false);

	const [searchParams] = useSearchParams();
	const vehicleId = searchParams.get("id") || "";
	const { mutate: getVehicleDocumentUrl } = useVehicleDocumentUrl();

	const vehicleDocumentsHistory = useQuery({
		queryKey: ["vehicle-documents-history", vehicleId],
		queryFn: () => getVehicleDocumentsHistory(Number(vehicleId)),
		initialData: {
			items: [],
			totalCount: 0,
			pageSize: 10,
			currentPage: 1,
			totalPages: 0,
		},
		enabled: !!vehicleId && historicoExpanded,
		refetchOnWindowFocus: false,
	});
	const columnsMainActions: Column<VehicleDocument>[] = useMemo(() => {
		const columnsMainActionsButton = (
			row: VehicleDocument,
			Icon: FC<ComponentPropsWithRef<"svg">>,
			onClick: (document: VehicleDocument) => void,
			tooltip: string
		) => (
			<ButtonUtility
				className="text-primary"
				color="tertiary"
				icon={Icon}
				onClick={() => onClick(row)}
				size="xs"
				tooltip={tooltip}
			/>
		);

		return [
			{
				id: "actions",
				label: "Ações",
				render: (row) => (
					<div className="flex items-center gap-1">
						{columnsMainActionsButton(row, Edit05, onEdit, "Editar")}
						{columnsMainActionsButton(
							row,
							LinkExternal01,
							() =>
								getVehicleDocumentUrl({
									vehicleId,
									documentId: row.id,
									openUrl: true,
								}),
							"Visualizar"
						)}
						{columnsMainActionsButton(
							row,
							Download01,
							() =>
								getVehicleDocumentUrl({
									vehicleId,
									documentId: row.id,
								}),
							"Download"
						)}
					</div>
				),
			},
		];
	}, [onEdit, getVehicleDocumentUrl, vehicleId]);

	const columns: TableColumn<VehicleDocument>[] = useMemo(
		() => [
			{
				id: "name",
				label: "Tipo de Documento",
				minWidth: 200,
				render: (row) => vehicleDocumentTypeIdLabel(row.type),
			},
			{
				id: "createdAt",
				label: "Data de Cadastro",
				render: (row) => dayjs(row.createdAt).format("DD/MM/YYYY"),
			},
			{
				id: "paymentDate",
				label: "Data de Pagamento",
				render: (row) =>
					row.paymentDate ? dayjs(row.paymentDate).format("DD/MM/YYYY") : "-",
			},
			{
				id: "renewalDate",
				label: "Próxima Renovação",
				render: (row) =>
					row.renewalDate ? dayjs(row.renewalDate).format("DD/MM/YYYY") : "-",
			},
			{
				id: "situation",
				label: "Situação",
				render: (row) => (
					<Badge
						className={cx(
							"rounded-full px-1.5 py-2 font-medium leading-3",
							vehicleDocumentStatusColor(row?.status as VehicleDocumentStatus)
						)}
					>
						<div
							className={cx(
								"size-1.5 rounded-full",
								vehicleDocumentStatusDotColor(
									row?.status as VehicleDocumentStatus
								)
							)}
						/>
						{vehicleDocumentStatusLabel(row?.status as VehicleDocumentStatus)}
					</Badge>
				),
			},
			...columnsMainActions,
		],
		[columnsMainActions]
	);

	return (
		<section className="mx-6 rounded-lg border border-secondary_alt border-solid bg-white">
			<button
				aria-expanded={historicoExpanded}
				className={cx(
					"flex w-full items-center justify-between gap-2 overflow-hidden px-6 py-4 text-left",
					"font-semibold text-lg text-primary",
					"hover:bg-bg-tertiary focus-visible:outline-2 focus-visible:outline-fg-quaternary focus-visible:outline-offset-2 rounded-t-lg"
				)}
				onClick={() => setHistóricoExpanded((v) => !v)}
				type="button"
			>
				<div className="flex items-center gap-2">
					<span>Histórico de Documentos (Últimos 5 anos)</span>
					{vehicleDocumentsHistory.data.items.length > 0 && (
						<span className="rounded-md bg-fg-quaternary_hover px-2 py-0.5 font-medium text-white text-xs">
							{vehicleDocumentsHistory.data.items.length}
						</span>
					)}
				</div>
				{historicoExpanded ? (
					<ChevronDown className="size-5 shrink-0 text-fg-tertiary" />
				) : (
					<ChevronUp className="size-5 shrink-0 text-fg-tertiary" />
				)}
			</button>

			{historicoExpanded ? (
				<TableCard.Root className="rounded-xl ring-0" variant="listing">
					<Table aria-label="Documentos">
						<Table.Header columns={columns}>
							{(column) => (
								<Table.Head
									className="border-t-0"
									id={column.id}
									isRowHeader={columns.indexOf(column) === 0}
									key={column.id}
									label={column.label}
									style={{
										...(!!column?.minWidth && {
											minWidth: `${column.minWidth}px`,
										}),
									}}
								/>
							)}
						</Table.Header>

						<Table.Body>
							{vehicleDocumentsHistory.isFetching ? (
								<Table.Row>
									<Table.Cell
										className={TABLE_LISTING_STYLES.stateCell}
										colSpan={columns.length}
									>
										<LoadingIndicator size="md" type="line-simple" />
									</Table.Cell>
								</Table.Row>
							) : (
								vehicleDocumentsHistory.data.items?.length > 0 &&
								vehicleDocumentsHistory.data.items.map((item) => (
									<Table.Row columns={columns} id={item.id} key={item.id}>
										{(column) => (
											<Table.Cell className="whitespace-nowrap" key={column.id}>
												{column.render
													? column.render(item)
													: String(
															item[column.id as keyof VehicleDocument] ?? ""
														)}
											</Table.Cell>
										)}
									</Table.Row>
								))
							)}
							{vehicleDocumentsHistory.data.items?.length === 0 &&
								!vehicleDocumentsHistory.isFetching && (
									<Table.Row>
										<Table.Cell
											className={TABLE_LISTING_STYLES.stateCell}
											colSpan={columns.length}
										>
											<VehicleDocumentEmpty
												buttonText="Adicionar Documento"
												description="Clique no botão abaixo para adicionar o primeiro documento."
												onAdd={onAdd}
												title="Nenhum documento cadastrado!"
											/>
										</Table.Cell>
									</Table.Row>
								)}
						</Table.Body>
					</Table>
				</TableCard.Root>
			) : null}
		</section>
	);
}
